import React, { useState, useEffect, memo, Suspense, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Box } from "@mui/material";
import Navbar from "./Outils/Navbar";
import LoginPage from "./login";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Lazy-load pages
const Loader = React.lazy(() => import("./LoadingPage"));
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const MyAccount = React.lazy(() => import("./pages/MonCompte"));
const SitesPage = React.lazy(() => import("./pages/Admin/Sites"));
const EtablissementsPage = React.lazy(() => import("./pages/Admin/Etablissements"));
const EmployesPage = React.lazy(() => import("./pages/Admin/Employes"));
const ComptesPage = React.lazy(() => import("./pages/Admin/Comptes"));
const MainCourantePage = React.lazy(() => import("./pages/MainCourante"));
const VivreCruPage = React.lazy(() => import("./pages/VivreCru"));
const PrestationsConfig = React.lazy(() => import("./pages/Prestations"));
const NotesDeFraisPage = React.lazy(() => import("./pages/NotesDeFrais"));
const TableauDeBordPage = React.lazy(() => import("./pages/TableauDeBord"));
const Articles = React.lazy(() => import("./pages/Articles"));
const SiteProfilPage = React.lazy(() => import("./pages/SiteProfil"));
const FraisGenerauxPage = React.lazy(() => import("./pages/FraisGeneraux"));
const BudgetPage = React.lazy(() => import("./pages/Budgets"));
const SecteurProfilPage = React.lazy(() => import("./pages/SecteurProfil"));

// Custom hook for localStorage management.
const useLocalStorage = (key, initialValue) => {
  const [value, setValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage: ${error}`);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving to localStorage: ${error}`);
    }
  }, [key, value]);

  return [value, setValue];
};

const gouvernanteRoutes = [
  { path: "/dashboard", element: <LandingPage /> },
  { path: "/moncompte", element: <MyAccount /> },
];

const personnelRoutes = [
  { path: "/dashboard", element: <LandingPage /> },
  { path: "/moncompte", element: <MyAccount /> },
];

const chefRoutes = [
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/main-courante", element: <MainCourantePage /> },
  { path: "/vivre-cru", element: <VivreCruPage /> },
  { path: "/notes-de-frais", element: <NotesDeFraisPage /> },
  { path: "/articles", element: <Articles /> },
  { path: "/moncompte", element: <MyAccount /> },
  { path: "/prestations", element: <PrestationsConfig /> },
];

const RsRoutes = [
  { path: "/dashboard", element: <TableauDeBordPage /> },
  { path: "/main-courante", element: <MainCourantePage /> },
  { path: "/vivre-cru", element: <VivreCruPage /> },
  { path: "/notes-de-frais", element: <NotesDeFraisPage /> },
  { path: "/site/:siteId", element: <SiteProfilPage /> },
  { path: "/secteur/:secteurId", element: <SecteurProfilPage /> },
  { path: "/etablissements", element: <EtablissementsPage /> },
  { path: "/sites", element: <SitesPage /> },
  { path: "/employes", element: <EmployesPage /> },
  { path: "/moncompte", element: <MyAccount /> },
];

const adminRoutes = [
  { path: "/dashboard", element: <TableauDeBordPage /> },
  { path: "/main-courante", element: <MainCourantePage /> },
  { path: "/vivre-cru", element: <VivreCruPage /> },
  { path: "/comptes", element: <ComptesPage /> },
  { path: "/moncompte", element: <MyAccount /> },
  { path: "/prestations", element: <PrestationsConfig /> },
  { path: "/notes-de-frais", element: <NotesDeFraisPage /> },
  { path: "/tableau-de-bord", element: <TableauDeBordPage /> },
  { path: "/site/:siteId", element: <SiteProfilPage /> },
  { path: "/fraisGeneraux", element: <FraisGenerauxPage /> },
  { path: "/budget", element: <BudgetPage /> },
  { path: "/articles", element: <Articles /> },
  { path: "/etablissements", element: <EtablissementsPage /> },
  { path: "/sites", element: <SitesPage /> },
  { path: "/employes", element: <EmployesPage /> },
  { path: "/secteur/:secteurId", element: <SecteurProfilPage /> },
];

const AppContent = memo(({ userRole, setUserRole }) => {
  const location = useLocation();
  const bgColor = location.pathname === "/dashboard" ? "#999999" : "#ffffff";
  const showNavbar = location.pathname !== "/dashboard";
  const [lastVisitedPath, setLastVisitedPath] = useLocalStorage(
    "lastVisitedPath",
    "/home",
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    setLastVisitedPath(location.pathname);
  }, [location.pathname, setLastVisitedPath]);

  const routes = useMemo(() => {
    switch (userRole) {
      case "Gouvernante":
        return gouvernanteRoutes;
      case "Personnel":
        return personnelRoutes;
      case "Chef":
        return chefRoutes;
      case "Admin":
        return adminRoutes;
      case "RS":
        return RsRoutes;
      default:
        return [];
    }
  }, [userRole]);

  if (!userRole) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage setUserRole={setUserRole} />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }
  if (location.pathname === "/home") {
    return (
      <Routes>
        <Route path="/home" element={<Loader />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Navbar userRole={userRole} setUserRole={setUserRole} />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: bgColor,
          pt: showNavbar ? 10 : 0,
        }}
      >
        <Routes>
          <Route
            path="/home"
            element={<Loader userRole={userRole} setUserRole={setUserRole} />}
          />
          {routes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          <Route path="*" element={<Navigate to={lastVisitedPath} />} />
        </Routes>
      </Box>
    </>
  );
});

function App() {
  const [userRole, setUserRole] = useLocalStorage("role", null);

  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    if (!savedToken) {
      setUserRole(null);
    }
  }, [setUserRole]);

  return (
    <BrowserRouter>
      <AppContent userRole={userRole} setUserRole={setUserRole} />
    </BrowserRouter>
  );
}

export default memo(App);
